import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Accordion,
    Button,
    Card,
    ContentCard,
    Form,
    HeaderCard,
    Icon,
    Section,
    SelectHierarchy,
    SelectLabel,
    SubTitleCard,
    TitleCard,
    ToastContext,
    Message,
    WeezHeader
} from '@weezevent/nacre';
import { useForm } from '../../../utils/validators';
import { ModerationFlowsModel, ModerationOperators, ModerationTransitions, ModerationRole } from '../../../models';
import { pushOrSplice } from '../../../utils/tools';
import LoadingCard, { LoadingOptions } from '../../../components/loadingCard';

import Style from './operators.module.css';

const expectedCapacities = [
    'PyvarCapacity.MODERATION_ADMIN',
    'PyvarCapacity.MODERATION_ATTENDEES',
    'PyvarCapacity.MODERATION_ATTENDEES_LIGHT',
    'PyvarCapacity.MODERATION_ATTENDEES_EDIT',
    'PyvarCapacity.MODERATION_ATTENDEES_EDIT_LIGHT'
];

export default function OperatorDetails({ organizationId, operatorId, flowId }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const history = useHistory();
    const default_form = React.useMemo(() => {
        return {
            first_name: '',
            last_name: '',
            email: '',
            allowed_transitions: [],
            roles: [
                {
                    context: null,
                    role: null
                }
            ],
            client_id: window.weezConfig.connect.clientId
        };
    }, [organizationId]);
    const [formOperator, setFormOperator, setFormOperatorObject] = useForm({ ...default_form });

    ModerationOperators.useApiModel(
        {
            organizationId,
            flowId,
            id: operatorId,
            launch: Boolean(operatorId),
            cache: false,
            onSuccess: response => {
                setFormOperatorObject({ ...formOperator, ...response.object });
            }
        },
        [organizationId, operatorId, flowId]
    );

    const [moderationFlowsData, loadingFlows] = ModerationFlowsModel.useApiModel(
        {
            organizationId: organizationId,
            cache: false,
            allow_cache: false
        },
        [organizationId]
    );

    const [rolesData, loadingRoles] = ModerationRole.useApiModel(
        {
            organizationId
        },
        [organizationId]
    );

    const [transitions, loadingTransitions] = ModerationTransitions.useApiModel(
        {
            organizationId
        },
        [organizationId]
    );

    const rolesOptions = React.useMemo(() => {
        return rolesData?.filter(role => role.slug.startsWith('moderation-')).map(role => role.toOption());
    }, [rolesData, loadingRoles]);

    const transitionsOptions = React.useMemo(() => {
        if (!transitions || loadingTransitions || loadingFlows) {
            return [];
        }

        const selected_flows = formOperator.roles.filter(r => r.context?.length === 5).map(role => parseInt(role.context.at(-1)));

        return transitions
            ?.filter(tr => {
                if (!selected_flows.length) {
                    return tr;
                }
                return selected_flows.includes(tr.flow_id);
            })
            .map(tr => {
                const flow = moderationFlowsData.find(fl => fl.id === tr.flow_id);
                return tr.toOption(flow?.name);
            });
    }, [transitions, loadingTransitions, loadingFlows, formOperator, moderationFlowsData]);

    const selectedTransitionsLabel = React.useMemo(() => {
        const flowTransitions = transitionsOptions?.filter(tr => formOperator.allowed_transitions.includes(tr.value));

        if (!flowTransitions.length) {
            return null;
        }
        if (flowTransitions.length !== transitionsOptions?.length) {
            return t('ticket.moderation.operators.details.moderationTransitionsCount', { count: flowTransitions.length });
        }
        return t('ticket.moderation.operators.list.allAccess');
    }, [formOperator.allowed_transitions, transitionsOptions]);

    const transitionsItems = React.useMemo(() => transitionsOptions.length, [transitionsOptions]);

    const transitionsDisabled = React.useMemo(() => {
        if (!rolesData || loadingRoles || !formOperator.roles.length) {
            return true;
        }

        if (!formOperator.roles?.length || formOperator.roles?.some(r => !r.context || r.context?.length === 3)) {
            return false;
        }

        return !formOperator.roles.some(r => {
            if (!r.role) {
                return false;
            }
            const role = rolesData.find(rl => rl.slug === r.role);
            return role && role.capacities.some(cap => expectedCapacities.includes(cap.name));
        });
    }, [formOperator, rolesData, loadingRoles, expectedCapacities]);

    const backUri = React.useMemo(() => {
        if (!flowId) {
            return `/ticket/O${organizationId}/moderation/moderation_users`;
        }
        return `/ticket/O${organizationId}/moderation/moderation_flows/${flowId}/moderation_users`;
    }, [organizationId, flowId]);

    const handleRolesList = React.useCallback(
        value => {
            const roles = [...formOperator.roles];
            let addition = {
                context: ['*', '*', organizationId.toString()],
                role: null
            };
            const new_roles = pushOrSplice(roles, addition, value);
            return setFormOperator('roles', new_roles);
        },
        [formOperator, organizationId]
    );

    const handleRole = React.useCallback(
        (name, value, index) => {
            let roles = [...formOperator.roles];
            const realIndex = !operatorId || roles.length === 1 ? index : index + 1;

            switch (name) {
                case 'context':
                    if (value === 'all') {
                        roles[realIndex][name] = ['*', '*', organizationId.toString()];
                    } else {
                        roles[realIndex][name] = ['*', '*', organizationId.toString(), '*', value.toString()];
                    }
                    break;
                default:
                    roles[realIndex][name] = value.toString();
                    break;
            }

            setFormOperator('roles', roles);
        },
        [formOperator, organizationId]
    );

    const handleTransitions = React.useCallback(
        value => {
            const allTransitions = !formOperator.allowed_transitions.includes(value) ? value : null;

            setFormOperator('allowed_transitions', allTransitions);
        },
        [formOperator]
    );

    const handleSubmit = React.useCallback(
        (next = false) => {
            let operator = new ModerationOperators({ ...formOperator });
            operator.roles = formOperator.roles.filter(role => role.role && role.context && role.role !== 'moderation');

            // Remove operator.id if it exists to ensure a POST request is made for update instead of a PATCH request
            operatorId && delete operator.id;

            operator
                .save({ organizationId, flowId })
                .then(() => {
                    toast.success(t(`ticket.moderation.operators.details.toastSuccess`));
                    if (!next) {
                        history.push(backUri);
                    }
                })
                .catch(() => {
                    toast.error(t('ticket.moderation.operators.details.toastError'));
                });
        },
        [formOperator, organizationId]
    );

    return (
        <>
            <WeezHeader
                title={!operatorId ? t('ticket.moderation.operators.details.titleAdd') : t('ticket.moderation.operators.details.titleEdit')}
                rightComponent={[
                    <Button key={0} icon={<Icon name="plus" />} label={t('ticket.moderation.operators.details.addNewOperatorCta')} onClick={() => handleSubmit(true)} />,
                    <Button key={1} primary label={t('ticket.common.action.save')} onClick={() => handleSubmit()} />
                ]}
                backLink={{
                    onClick: () => {
                        history.push(backUri);
                    }
                }}
            />

            <Card>
                <HeaderCard separator>
                    <TitleCard title={t('ticket.moderation.operators.details.userCardTitle')} />
                    <SubTitleCard title={t('ticket.moderation.operators.details.userCardSubtitle')} />
                </HeaderCard>
                <ContentCard>
                    <Form.Field>
                        <Form.Label label={t('ticket.moderation.operators.details.firstNameLabel')} />
                        <Form.Input
                            name="first_name"
                            placeholder={t('ticket.moderation.operators.details.firstNamePlaceholder')}
                            value={formOperator?.first_name}
                            onChange={({ target: { value } }) => {
                                setFormOperator('first_name', value);
                            }}
                        />
                    </Form.Field>
                    <Form.Field>
                        <Form.Label label={t('ticket.moderation.operators.details.lastNameLabel')} />
                        <Form.Input
                            name="last_name"
                            placeholder={t('ticket.moderation.operators.details.lastNamePlaceholder')}
                            value={formOperator.last_name}
                            onChange={({ target: { value } }) => {
                                setFormOperator('last_name', value);
                            }}
                        />
                    </Form.Field>
                    <Form.Field>
                        <Form.Label label={t('ticket.moderation.operators.details.emailLabel')} />
                        <Form.Input
                            name="email"
                            placeholder={t('ticket.moderation.operators.details.emailPlaceholder')}
                            value={formOperator.email}
                            onChange={({ target: { value } }) => {
                                setFormOperator('email', value);
                            }}
                        />
                    </Form.Field>
                </ContentCard>
            </Card>
            <Card>
                <HeaderCard separator>
                    <TitleCard title={t('ticket.moderation.operators.details.accessCardTitle')} />
                    <SubTitleCard title={t('ticket.moderation.operators.details.accessCardSubtitle')} />
                </HeaderCard>
                <ContentCard className={Style['operator-moderation-cardsContainer']}>
                    {formOperator.roles
                        .filter(role => role.role !== 'moderation')
                        .map((role, index) => (
                            <AccessModerationRender
                                key={index}
                                role={role}
                                index={index}
                                loadingTransitions={loadingTransitions}
                                loadingRoles={loadingRoles}
                                handleRolesList={handleRolesList}
                                rolesOptions={rolesOptions}
                                handleRole={handleRole}
                                moderationFlowsData={moderationFlowsData}
                                loadingFlowsloadingFlows
                            />
                        ))}
                    <button className={Style['operator-moderation-addModerationCta']} onClick={() => handleRolesList()}>
                        <Icon name="plus" />
                        <span>{t('ticket.moderation.operators.details.addNewModerationCta')}</span>
                    </button>
                </ContentCard>
            </Card>
            <Card>
                <HeaderCard separator>
                    <TitleCard title={t('ticket.moderation.operators.details.moderationTransitionsLabel')} />
                    <SubTitleCard title={t('ticket.moderation.operators.details.moderationTransitionsTooltip')} />
                </HeaderCard>
                <ContentCard>
                    <LoadingCard loading={loadingFlows || loadingTransitions || loadingRoles}>
                        <Form.Field>
                            <Message title={t('ticket.moderation.operators.details.message.title')} type="default" visible={transitionsDisabled}>
                                {t('ticket.moderation.operators.details.message.description')}
                            </Message>
                            <SelectHierarchy
                                multi
                                onChange={value => handleTransitions(value)}
                                options={transitionsOptions}
                                disabled={transitionsDisabled}
                                value={selectedTransitionsLabel}
                                selected={formOperator.allowed_transitions}
                                placeholder={t('ticket.common.action.select')}
                                totalItems={transitionsItems}
                                labelApply={t('ticket.common.action.select')}
                                labelSelectAll={t('ticket.common.action.select-all')}
                            />
                        </Form.Field>
                    </LoadingCard>
                </ContentCard>
            </Card>
        </>
    );
}

const AccessModerationRender = React.memo(({ role, index, loadingTransitions, loadingRoles, handleRolesList, rolesOptions, handleRole, moderationFlowsData, loadingFlows }) => {
    const { t } = useTranslation();

    const moderationFlowsOptions = React.useMemo(() => {
        if (!moderationFlowsData || loadingFlows) {
            return LoadingOptions;
        }
        const flows = moderationFlowsData?.map(flow => ({
            text: flow.name,
            value: flow.id
        }));
        flows.unshift({
            text: t('ticket.moderation.operators.details.selectAllFlows'),
            value: 'all'
        });
        return flows;
    }, [moderationFlowsData, loadingFlows, LoadingOptions]);

    const contextValue = React.useMemo(() => {
        if (role.context?.length === 5) {
            return parseInt(role.context.at(-1));
        } else if (role.role && role.context?.length === 3) {
            return 'all';
        }
        return null;
    }, [role.context, role.role]);

    return (
        <Accordion
            className={Style['operator-moderation-subcard']}
            openByDefault
            title={
                <div className={Style['operator-moderation-card']}>
                    <b>{`${t('ticket.moderation.operators.details.moderationNameLabel')} ${index + 1}`}</b>
                    <div className={Style['operator-moderation-icons']}>
                        <Icon name="trash" onClick={() => handleRolesList(index)} />
                    </div>
                </div>
            }
        >
            <Section>
                <LoadingCard loading={loadingFlows || loadingTransitions || loadingRoles}>
                    <div className={Style['operator-moderation-cardContent']}>
                        <span>{t('ticket.moderation.operators.details.moderationNameLabel')}</span>
                        <SelectHierarchy
                            className={Style['operator-moderation-selectFlows']}
                            value={contextValue}
                            options={moderationFlowsOptions}
                            placeholder={t('ticket.common.action.select')}
                            height="maxi"
                            onChange={(_, { value }) => handleRole('context', value, index)}
                        />
                        <SelectLabel
                            value={role.role}
                            label={t('ticket.moderation.operators.details.moderationRoleLabel')}
                            onChange={value => handleRole('role', value, index)}
                            options={rolesOptions}
                            placeholder={t('ticket.common.action.select')}
                        />
                    </div>
                </LoadingCard>
            </Section>
        </Accordion>
    );
});
